import React from "react";

interface TimelineEvent {
  title: string;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  events?: string | TimelineEvent[];
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultEvents: TimelineEvent[] = [
  {
    title: "",
    description: "",
    icon: "1️⃣",
  },
  {
    title: "",
    description: "",
    icon: "2️⃣",
  },
  {
    title: "",
    description: "",
    icon: "3️⃣",
  },
  {
    title: "",
    description: "",
    icon: "4️⃣",
  },
];

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const TimelineSlide1 = {
  id: "timeline-slide1",
  name: "Timeline Style 1",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "events",
      label: "Events",
      type: "text",
      defaultValue: JSON.stringify(defaultEvents),
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      events = JSON.stringify(defaultEvents),
      bgColor = "linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)",
      onEdit = () => {},
    } = content;

    let parsedEvents = defaultEvents;
    try {
      if (typeof events === "string") {
        parsedEvents = JSON.parse(events);
      } else if (Array.isArray(events)) {
        parsedEvents = events;
      }
    } catch (error) {
      console.warn("Error parsing events:", error);
    }

    const handleEventEdit = (index: number, field: string, value: string) => {
      const updatedEvents = [...parsedEvents];
      updatedEvents[index] = {
        ...updatedEvents[index],
        [field]: value,
      };
      onEdit({ ...content, events: JSON.stringify(updatedEvents) });
    };

    // Calculate dynamic styles based on number of events
    const getEventStyles = (totalEvents: number) => {
      if (totalEvents <= 4)
        return {
          spacing: "space-y-8",
          padding: "p-6",
          titleSize: "text-xl",
          descSize: "text-base",
          iconSize: "text-3xl",
          cardPadding: "py-4 space-y-8",
        };

      if (totalEvents <= 6)
        return {
          spacing: "space-y-4",
          padding: "p-4",
          titleSize: "text-lg",
          descSize: "text-sm",
          iconSize: "text-2xl",
          cardPadding: "py-2 space-y-4",
        };

      return {
        spacing: "space-y-3",
        padding: "p-3",
        titleSize: "text-base",
        descSize: "text-xs",
        iconSize: "text-xl",
        cardPadding: "py-1 space-y-3",
      };
    };

    const styles = getEventStyles(parsedEvents.length);

    return (
      <div className="w-full h-full relative overflow-hidden text-white">
        {/* Dynamic Background */}
        <div
          className="absolute inset-0 transition-all duration-1000"
          style={{ background: bgColor }}
        >
          {/* Animated Patterns */}
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute w-full h-full"
              style={{
                backgroundImage: `
                  radial-gradient(circle at 20% 20%, #22c55e80 0%, transparent 25%),
                  radial-gradient(circle at 80% 80%, #16a34a80 0%, transparent 25%),
                  radial-gradient(circle at 50% 50%, #15803d80 0%, transparent 35%)
                `,
              }}
            />
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <EditableDiv
              text={title}
              onEdit={(value) => onEdit({ ...content, title: value })}
              className="text-4xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r 
                        from-white via-green-100 to-white animate-text-shine"
            />
            <EditableDiv
              text={subtitle}
              onEdit={(value) => onEdit({ ...content, subtitle: value })}
              className="text-xl text-green-100/90 font-light"
            />
          </div>

          {/* Timeline */}
          <div className="flex-1 relative">
            {/* Center Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-transparent via-white/30 to-transparent" />

            {/* Events */}
            <div className={`relative ${styles.cardPadding}`}>
              {parsedEvents.map((event, idx) => {
                const isLeft = idx % 2 === 0;
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-6 group ${
                      isLeft ? "flex-row" : "flex-row-reverse"
                    }`}
                  >
                    {/* Event Content */}
                    <div
                      className={`flex-1 ${isLeft ? "text-right" : "text-left"}`}
                    >
                      <div
                        className={`relative bg-white/5 backdrop-blur-md rounded-2xl ${styles.padding}
                                transform transition-all duration-500 hover:scale-102
                                border border-white/10 hover:border-white/20
                                hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]`}
                        style={{
                          background: `linear-gradient(${isLeft ? "-135deg" : "135deg"}, 
                            #22c55e20 0%, 
                            #22c55e10 40%, 
                            transparent 100%
                          )`,
                        }}
                      >
                        <div
                          className={`flex items-center gap-2 mb-2 ${
                            isLeft ? "flex-row-reverse" : "flex-row"
                          }`}
                        >
                          <EditableDiv
                            text={event.icon}
                            onEdit={(value) =>
                              handleEventEdit(idx, "icon", value)
                            }
                            className={`${styles.iconSize} transform transition-all duration-500 
                                    group-hover:scale-110 group-hover:rotate-12`}
                          />
                          <EditableDiv
                            text={event.title}
                            onEdit={(value) =>
                              handleEventEdit(idx, "title", value)
                            }
                            className={`${styles.titleSize} font-bold text-white bg-gradient-to-r 
                                    from-white to-green-100 bg-clip-text text-transparent`}
                          />
                        </div>

                        <EditableDiv
                          text={event.description}
                          onEdit={(value) =>
                            handleEventEdit(idx, "description", value)
                          }
                          className={`${styles.descSize} text-white/80 leading-relaxed
                                  transform transition-all duration-300 group-hover:text-white
                                  ${isLeft ? "pr-8" : "pl-8"}`}
                        />

                        {/* Decorative Elements */}
                        <div
                          className={`absolute top-0 ${isLeft ? "left-0" : "right-0"} 
                                    w-20 h-20 opacity-20 rounded-full blur-2xl transition-all 
                                    duration-500 group-hover:opacity-30`}
                          style={{ background: "#22c55e" }}
                        />
                      </div>
                    </div>

                    {/* Timeline Node */}
                    <div className="relative z-10">
                      <div
                        className="w-3 h-3 rounded-full bg-white/30 backdrop-blur-sm
                                  transform transition-all duration-500 group-hover:scale-150
                                  border border-white/50 group-hover:border-white/80"
                      />
                      <div
                        className="absolute inset-0 rounded-full animate-ping 
                                  opacity-75 bg-white/20"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  },
};

export default TimelineSlide1;
